import { Controller } from "@hotwired/stimulus"
import mapboxgl from "mapbox-gl"
import { MAP_STYLE, MAP_CONFIG } from "../custom/map_style"

// The sites index map view (TASK-199.3, decision-48, wireframe board 9).
//
// One clustered GeoJSON source drawn as circle layers — never DOM markers, so
// a team with thousands of sites costs the same as one with ten. Follows
// map_picker_controller's shape: token value, refresh() on reveal, remove()
// on disconnect. The base comes from custom/map_style — the one look every
// map in the app shares (TASK-202): Mapbox Standard, monochrome, so the
// orange and status-coloured pins carry the colour.
//
// The data contract: `pinsUrl` answers a FeatureCollection whose properties
// are { id, name, uprn, postcode } plus a top-level `unlocated_count`
// (Sites::PinsController). Every panel row is built from those properties
// alone — no second request per row. `previewUrl` holds an `__ID__`
// placeholder; the Selected card is a turbo-frame (`site-preview`) pointed
// at it.
//
// Search in map view: input events from the search box bubble to the toolbar
// (data-action="input->sites-map#search"), are debounced like frame-filter's
// (300ms), refetch the pins with the form's params and setData — no page
// visit. The address bar follows via replaceState. Chip removal and the
// filter slideover's Apply stay full visits.
//
// TASK-199.4:
// - The unlocated block is a turbo-frame (`unlocatedFrame`). A search points
//   it at `unlocatedUrl`; a write stream (map picker, Edit, New Site) refreshes
//   it with refresh_frame. A reload this controller did not ask for is the
//   save-refresh hook: it refetches the pins, so a site given a location
//   leaves the list and appears on the map without a page reload.
// - States: Mapbox failing before its style loads (bad token, no WebGL,
//   network) or the pins failing reveals the `error` callout in place of the
//   canvas; no located sites fits Great Britain and shows the `empty` card.
// - Phones: `phoneTableUrl` is only rendered when the map came from the
//   member's STORED choice. Below md this replaces the visit with that table
//   URL before Mapbox mounts (decision-48: phones always open the table).
// - Below lg the panel is a bottom sheet (`panel`, data-expanded): a peek
//   line that expands to the panel; the map resizes once it settles.
//
// TASK-199.5 — compliance mode (`compliance` value, from ?compliance=):
// - Pins then carry properties.compliance ("overdue" | "due_soon" | "clear" |
//   "none") and `site-pins` colours by it in the status tokens. Without the
//   mode the property is absent and pins and clusters are the map-marker
//   orange (TASK-202, DESIGN.md).
// - Clusters aggregate the WORST member via clusterProperties (a max over a
//   rank), because a same-coordinate stack never renders its pins singly:
//   its cluster is the only place their status can show. A neutral cluster
//   would hide an overdue building inside an estate.
// - The selected pin keeps its status colour and enlarges, ringed in
//   blue-800 so selection still reads in the brand colour.
// - The legend is server-rendered beside the canvas (sites/map).
//
// Connects to data-controller="sites-map"
const SOURCE = "sites"
const CLUSTER_MAX_ZOOM = 14
const IN_VIEW_LIMIT = 100
// Orange is the map-marker colour (DESIGN.md, TASK-202): orange-600 pins,
// orange-700 for the selected pin AND for clusters — a cluster carries 12px
// white count text, which needs 4.5:1 (orange-700 is 4.91:1, orange-600 only
// 3.64:1). blue-800 stays for the ring that marks selection over a status
// colour in compliance mode.
const ORANGE_600 = "#E05E0B"
const ORANGE_700 = "#C24A0C"
const BLUE_800 = "#134d79"
// DESIGN.md status tokens: --color-status-danger / -warning / -success, and
// gray-500 for a site nothing is scheduled at (white cluster counts stay
// legible on it, which gray-400 would not). Keep in step with
// SitesIndexHelper::COMPLIANCE_BADGE_COLOURS and the legend in sites/map.
const RED_600 = "#dc2626"
const AMBER_600 = "#d97706"
const GREEN_600 = "#16a34a"
const GRAY_500 = "#6b7280"
const COMPLIANCE_COLOURS = { overdue: RED_600, due_soon: AMBER_600, clear: GREEN_600, none: GRAY_500 }
// Worst first: a cluster takes the max rank of its members.
const COMPLIANCE_RANK = ["match", ["get", "compliance"], "overdue", 3, "due_soon", 2, "clear", 1, 0]
const PIN_COMPLIANCE_COLOUR = [
  "match", ["get", "compliance"],
  "overdue", RED_600, "due_soon", AMBER_600, "clear", GREEN_600,
  GRAY_500
]
const CLUSTER_COMPLIANCE_COLOUR = [
  "match", ["get", "worst"],
  3, RED_600, 2, AMBER_600, 1, GREEN_600,
  GRAY_500
]
const FIT_OPTIONS = { padding: 60, maxZoom: 15 }
// Great Britain, for the instant before the pins arrive, and the view a
// world with no located sites is fitted to.
const DEFAULT_CENTRE = [-2.5, 54.0]
const DEFAULT_ZOOM = 5
const GB_BOUNDS = [[-8.65, 49.86], [1.77, 60.86]]
// Tailwind's md and lg — the phone bounce and the bottom sheet.
const PHONE_QUERY = "(max-width: 767.98px)"
const SHEET_QUERY = "(max-width: 1023.98px)"
const EMPTY = { type: "FeatureCollection", features: [] }

export default class extends Controller {
  static targets = [
    "map",
    "inViewCount",
    "inViewHeading",
    "inViewList",
    "inViewMore",
    "selectedEmpty",
    "preview",
    "mapSection",
    "unlocatedFrame",
    "unlocatedNote",
    "unlocatedCount",
    "empty",
    "emptyHeading",
    "emptyBody",
    "error",
    "panel",
    "sheetToggle",
    "sheetChevron"
  ]

  static values = {
    token: String,
    pinsUrl: String,
    unlocatedUrl: String,
    phoneTableUrl: String,
    indexUrl: String,
    previewUrl: String,
    params: Object,
    compliance: Boolean,
    debounce: { type: Number, default: 300 }
  }

  connect() {
    // Stored map, phone-sized viewport: open the table instead, before any
    // Mapbox work. Not from a Turbo preview (a cached snapshot shown while the
    // real page loads) — the real render decides.
    if (this.phoneTableUrlValue && window.matchMedia(PHONE_QUERY).matches &&
        !document.documentElement.hasAttribute("data-turbo-preview")) {
      this.bounced = true
      if (window.Turbo) {
        window.Turbo.visit(this.phoneTableUrlValue, { action: "replace" })
      } else {
        window.location.replace(this.phoneTableUrlValue)
      }
      return
    }

    // The server renders the token-missing callout instead of this
    // controller; a blank token here is a page built wrong, not a state.
    if (!this.tokenValue) return

    this.features = []
    this.selectedId = null
    this.stack = null
    this.failed = false
    this.styleReady = false
    this.loaded = false
    // A shared link carries its camera in #map=zoom/lat/lng. Honour it:
    // never fitBounds over a camera somebody chose.
    const camera = this.cameraFromHash()
    this.cameraPinned = camera !== null

    // Turbo snapshots the page before this controller disconnects, so a
    // restoration visit (Back) hands us the old map's canvas and the old
    // selection. Start clean: Mapbox wants an empty container, and the
    // selection lives in this instance, not in the DOM.
    this.mapTarget.replaceChildren()
    this.mapTarget.hidden = false
    if (this.hasErrorTarget) this.errorTarget.hidden = true
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
    for (const section of this.mapSectionTargets) section.hidden = false
    for (const note of this.unlocatedNoteTargets) note.firstElementChild.hidden = false
    if (this.hasSelectedEmptyTarget) this.selectedEmptyTarget.hidden = false
    if (this.hasPreviewTarget) {
      this.previewTarget.removeAttribute("src")
      this.previewTarget.replaceChildren()
    }
    this.setSheet(false)

    mapboxgl.accessToken = this.tokenValue
    try {
      this.map = new mapboxgl.Map({
        container: this.mapTarget,
        style: MAP_STYLE,
        config: MAP_CONFIG,
        center: camera?.center || DEFAULT_CENTRE,
        zoom: camera?.zoom ?? DEFAULT_ZOOM
      })
    } catch (error) {
      // No WebGL, or a browser mapbox-gl will not run in.
      this.fail(error)
      return
    }
    this.map.addControl(new mapboxgl.NavigationControl(), "top-right")

    // A failure before the style loads — a rejected token, the style or its
    // sprites unreachable — leaves a blank canvas forever. Say so instead.
    // After that, a stray tile error is Mapbox's to retry, not a dead map.
    this.map.on("style.load", () => { this.styleReady = true })
    this.map.on("error", (event) => {
      if (!this.styleReady) this.fail(event.error || event)
    })

    this.popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 14,
      className: "map-popup-flat"
    })

    this.map.on("load", () => this.mapLoaded())

    // mapbox-gl renders 0×0 inside a hidden or resizing container; the
    // established fix (map_picker, patch_map) is resize() once it is shown.
    this.resizeObserver = new ResizeObserver(() => this.refresh())
    this.resizeObserver.observe(this.mapTarget)
    this.onVisibility = () => {
      if (document.visibilityState === "visible") this.refresh()
    }
    document.addEventListener("visibilitychange", this.onVisibility)
  }

  disconnect() {
    if (this.bounced) return
    if (this.searchTimer) clearTimeout(this.searchTimer)
    if (this.fetchController) this.fetchController.abort()
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.onVisibility) document.removeEventListener("visibilitychange", this.onVisibility)
    if (this.popup) this.popup.remove()
    if (this.map) this.map.remove()
    this.map = null
  }

  // Re-fit after the container becomes visible or changes size.
  refresh() {
    if (!this.map) return
    requestAnimationFrame(() => this.map?.resize())
  }

  // The map cannot be shown: swap the callout in for the canvas. The panel
  // stays — the unlocated block's actions work without a map.
  fail(error) {
    console.error("sites-map: map unavailable", error)
    if (this.failed) return
    this.failed = true

    if (this.fetchController) this.fetchController.abort()
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.popup) this.popup.remove()
    if (this.map) this.map.remove()
    this.map = null
    this.loaded = false

    this.mapTarget.hidden = true
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
    if (this.hasErrorTarget) this.errorTarget.hidden = false
    for (const target of this.inViewCountTargets) target.textContent = ""
    // "· N without a location" loses its separator with nothing before it.
    for (const note of this.unlocatedNoteTargets) note.firstElementChild.hidden = true
    for (const section of this.mapSectionTargets) section.hidden = true
  }

  // ---------------------------------------------------------------- setup --

  mapLoaded() {
    const compliance = this.complianceValue
    this.map.addSource(SOURCE, {
      type: "geojson",
      data: EMPTY,
      cluster: true,
      clusterRadius: 40,
      clusterMaxZoom: CLUSTER_MAX_ZOOM,
      ...(compliance ? { clusterProperties: { worst: ["max", COMPLIANCE_RANK] } } : {})
    })

    this.map.addLayer({
      id: "site-clusters",
      type: "circle",
      source: SOURCE,
      filter: ["has", "point_count"],
      paint: {
        "circle-color": compliance ? CLUSTER_COMPLIANCE_COLOUR : ORANGE_700,
        // 2–9 / 10–49 / 50+
        "circle-radius": ["step", ["get", "point_count"], 16, 10, 20, 50, 26],
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2
      }
    })

    this.map.addLayer({
      id: "site-cluster-counts",
      type: "symbol",
      source: SOURCE,
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font": ["DIN Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 12,
        "text-allow-overlap": true
      },
      paint: { "text-color": "#ffffff" }
    })

    this.map.addLayer({
      id: "site-pins",
      type: "circle",
      source: SOURCE,
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": compliance ? PIN_COMPLIANCE_COLOUR : ORANGE_600,
        "circle-radius": 8,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2
      }
    })

    // The selected pin, drawn over the rest. Filter-driven rather than
    // feature-state: feature-state needs a numeric feature id, and a site's
    // id is a UUID.
    this.map.addLayer({
      id: "site-pin-selected",
      type: "circle",
      source: SOURCE,
      filter: this.selectedFilter(null),
      paint: compliance
        ? {
            // The status stays; selection is size plus a blue-800 ring.
            "circle-color": PIN_COMPLIANCE_COLOUR,
            "circle-radius": 11,
            "circle-stroke-color": BLUE_800,
            "circle-stroke-width": 3
          }
        : {
            "circle-color": ORANGE_700,
            "circle-radius": 11,
            "circle-stroke-color": "#ffffff",
            "circle-stroke-width": 3
          }
    })

    for (const layer of ["site-pins", "site-pin-selected"]) {
      this.map.on("mouseenter", layer, (event) => this.hoverPin(event))
      this.map.on("mouseleave", layer, () => this.unhover())
      this.map.on("click", layer, (event) => this.clickPin(event))
    }
    this.map.on("mouseenter", "site-clusters", () => { this.map.getCanvas().style.cursor = "pointer" })
    this.map.on("mouseleave", "site-clusters", () => { this.map.getCanvas().style.cursor = "" })
    this.map.on("click", "site-clusters", (event) => this.clickCluster(event))

    // The In-view list reads the loaded GeoJSON against the viewport, not
    // the rendered tiles, so it needs no wait for clustering to settle.
    this.map.on("moveend", () => this.moved())

    this.loaded = true
    this.loadPins({ fit: !this.cameraPinned })
  }

  // ----------------------------------------------------------------- data --

  // The search box, debounced. Reads the whole search form — the query plus
  // the hidden filter fields the search bar keeps in sync — so a search
  // narrows what is on screen rather than resetting the filters.
  search(event) {
    const form = event.target.closest("form")
    if (!form || this.failed) return

    if (this.searchTimer) clearTimeout(this.searchTimer)
    this.searchTimer = setTimeout(() => {
      const params = {}
      for (const [key, value] of new FormData(form).entries()) {
        if (value === "" || value === null) continue
        const name = key.endsWith("[]") ? key.slice(0, -2) : key
        if (key.endsWith("[]")) {
          params[name] = [ ...(params[name] || []), value ]
        } else {
          params[name] = value
        }
      }
      this.paramsValue = params
      this.replaceAddress()
      this.reloadUnlocated()
      // Before the style loads there is no source to feed; mapLoaded's own
      // fetch reads the params just stored.
      if (this.loaded) this.loadPins({ fit: !this.cameraPinned })
    }, this.debounceValue)
  }

  // Point the unlocated block at the current search. Its load is ours, so
  // unlocatedLoaded must not refetch the pins the search already fetches.
  reloadUnlocated() {
    if (!this.hasUnlocatedFrameTarget || !this.unlocatedUrlValue) return
    this.ownUnlocatedLoad = true
    this.unlocatedFrameTarget.src = this.urlWith(this.unlocatedUrlValue)
  }

  // The unlocated frame finished loading. When a write stream asked for it
  // (refresh_frame after a map-picker save, an Edit, a New Site), the sites
  // on the map changed too: refetch the pins, keeping the camera.
  unlocatedLoaded() {
    if (this.ownUnlocatedLoad) {
      this.ownUnlocatedLoad = false
      return
    }
    if (this.loaded) this.loadPins({ fit: false })
  }

  async loadPins({ fit }) {
    if (this.fetchController) this.fetchController.abort()
    this.fetchController = new AbortController()

    try {
      const response = await fetch(this.urlWith(this.pinsUrlValue), {
        headers: { Accept: "application/json" },
        signal: this.fetchController.signal
      })
      if (!response.ok) throw new Error(`pins answered ${response.status}`)
      const data = await response.json()
      if (!this.map) return

      this.features = data.features || []
      this.map.getSource(SOURCE)?.setData({ type: "FeatureCollection", features: this.features })
      this.updateUnlocated(data.unlocated_count)
      this.updateEmpty(data.unlocated_count)

      // A selection the new search excluded is no longer on the map.
      if (this.selectedId && !this.features.some((f) => f.properties.id === this.selectedId)) {
        this.clearSelection()
      }

      if (fit) this.fitToPins()
      this.renderInView()
    } catch (error) {
      if (error.name !== "AbortError") this.fail(error)
    }
  }

  // Frame the pins; with none, frame Great Britain rather than leave the
  // camera wherever the last search put it.
  fitToPins() {
    if (this.features.length === 0) {
      this.map.fitBounds(GB_BOUNDS, { padding: 20, duration: 0 })
      return
    }

    const bounds = new mapboxgl.LngLatBounds()
    for (const feature of this.features) bounds.extend(feature.geometry.coordinates)
    this.map.fitBounds(bounds, { ...FIT_OPTIONS, duration: 0 })
  }

  // No pins: say why over the map. Sites without a location are the usual
  // reason — then the card points at the unlocated block, which the sheet
  // opens to on a phone.
  updateEmpty(unlocatedCount) {
    if (!this.hasEmptyTarget) return

    const empty = this.features.length === 0
    this.emptyTarget.hidden = !empty
    if (!empty) return

    const narrowed = Object.keys(this.paramsValue).some((key) => !["column", "direction"].includes(key))
    if (unlocatedCount > 0) {
      this.emptyHeadingTarget.textContent = narrowed
        ? "None of these sites has a map location yet"
        : "None of your sites has a map location yet"
      this.emptyBodyTarget.textContent = "Set a location from the list to put a site on the map."
      if (this.sheetMode()) this.setSheet(true)
    } else {
      this.emptyHeadingTarget.textContent = narrowed ? "No sites match" : "No sites yet"
      this.emptyBodyTarget.textContent = narrowed
        ? "Nothing matches this search and these filters."
        : "Sites you add appear here once they have an address."
    }
  }

  // ---------------------------------------------------------------- camera --
  //
  // The camera lives in the address bar as #map=zoom/lat/lng — Mapbox's own
  // hash format, written here rather than by Mapbox's `hash` option. That
  // option's Hash#remove() writes the hash once more as the map is torn
  // down, and on a Turbo visit that is AFTER the address bar has moved on:
  // every page left from the map inherited a #map= fragment. Written here,
  // a move only touches the address bar while it is still the index.

  cameraFromHash() {
    const match = window.location.hash.match(/(?:^#|&)map=([^&]*)/)
    if (!match) return null
    const [zoom, lat, lng] = match[1].split("/").map(Number)
    if (![zoom, lat, lng].every(Number.isFinite)) return null
    return { zoom, center: [lng, lat] }
  }

  writeCamera() {
    if (!this.map || window.location.pathname !== new URL(this.indexUrlValue, window.location.origin).pathname) return

    const zoom = Math.round(this.map.getZoom() * 100) / 100
    // Mapbox's precision rule: enough decimals to place a pixel at this zoom.
    const m = Math.pow(10, Math.ceil((zoom * Math.LN2 + Math.log(512 / 360 / 0.5)) / Math.LN10))
    const { lat, lng } = this.map.getCenter()
    const camera = `map=${zoom}/${Math.round(lat * m) / m}/${Math.round(lng * m) / m}`

    const others = window.location.hash.replace(/^#/, "").split("&").filter((part) => part && !part.startsWith("map="))
    const url = new URL(window.location.href)
    url.hash = [ camera, ...others ].join("&")
    window.history.replaceState(window.history.state, "", url.toString())
  }

  // The address bar carries the filters and search (never ?view — that is
  // stored, not shared) and keeps the camera hash.
  replaceAddress() {
    const url = new URL(this.urlWith(this.indexUrlValue), window.location.origin)
    url.hash = window.location.hash
    window.history.replaceState(window.history.state, "", url.toString())
  }

  urlWith(base) {
    const url = new URL(base, window.location.origin)
    for (const [key, value] of Object.entries(this.paramsValue)) {
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(`${key}[]`, item)
      } else if (value !== null && value !== undefined && value !== "") {
        url.searchParams.set(key, value)
      }
    }
    return url.pathname + url.search
  }

  // The "N without a location" counts in the toolbar and the peek line. The
  // block itself is a frame the server renders.
  updateUnlocated(count) {
    if (!Number.isInteger(count)) return
    for (const note of this.unlocatedNoteTargets) note.hidden = count === 0
    for (const target of this.unlocatedCountTargets) target.textContent = `${count} without a location`
  }

  // The toolbar's count: bring the block into view — on a phone by opening
  // the sheet first.
  showUnlocated() {
    if (this.sheetMode()) this.setSheet(true)
    if (this.hasUnlocatedFrameTarget) this.unlocatedFrameTarget.scrollIntoView({ block: "nearest" })
  }

  // ---------------------------------------------------------------- sheet --

  sheetMode() {
    return window.matchMedia(SHEET_QUERY).matches
  }

  toggleSheet() {
    this.setSheet(!this.panelTarget.hasAttribute("data-expanded"))
  }

  setSheet(expanded) {
    if (!this.hasPanelTarget) return
    this.panelTarget.toggleAttribute("data-expanded", expanded)
    if (!expanded) this.panelTarget.scrollTop = 0
    if (this.hasSheetToggleTarget) this.sheetToggleTarget.setAttribute("aria-expanded", expanded ? "true" : "false")
    if (this.hasSheetChevronTarget) this.sheetChevronTarget.classList.toggle("rotate-180", expanded)
  }

  // The sheet finished sliding. It overlays the map rather than resizing it,
  // but resize() is cheap and keeps the canvas honest after any layout
  // change; then lift a selected pin the expanded sheet would cover.
  sheetSettled(event) {
    if (event.target !== this.panelTarget) return
    this.refresh()
    if (this.panelTarget.hasAttribute("data-expanded")) this.keepSelectedClear()
  }

  keepSelectedClear() {
    if (!this.map || !this.selectedId) return
    const feature = this.features.find((f) => f.properties.id === this.selectedId)
    if (!feature) return

    const point = this.map.project(feature.geometry.coordinates)
    const sheetTop = this.panelTarget.getBoundingClientRect().top - this.mapTarget.getBoundingClientRect().top
    if (point.y > sheetTop - 24) this.map.panBy([0, point.y - sheetTop / 2])
  }

  // ------------------------------------------------------------ selection --

  hoverPin(event) {
    this.map.getCanvas().style.cursor = "pointer"
    const feature = event.features[0]
    this.popup
      .setLngLat(feature.geometry.coordinates)
      .setText(feature.properties.name)
      .addTo(this.map)
  }

  unhover() {
    this.map.getCanvas().style.cursor = ""
    this.popup.remove()
  }

  // A pin outside a listed stack returns the panel to what is in view —
  // the stack list would otherwise sit beside a selection it does not hold.
  clickPin(event) {
    const id = event.features[0].properties.id
    if (this.stack && !this.stack.some((leaf) => leaf.properties.id === id)) {
      this.stack = null
      this.renderInView()
    }
    this.select(id)
  }

  // A cluster zooms in until it breaks apart. One that only breaks apart past
  // clusterMaxZoom is a stack — sites sharing a spot (a same-postcode estate)
  // that no zoom will separate. Those list their members in the panel
  // instead (decision-48: no spiderfy, no placement nudge).
  clickCluster(event) {
    const feature = event.features[0]
    const clusterId = feature.properties.cluster_id
    const source = this.map.getSource(SOURCE)

    source.getClusterExpansionZoom(clusterId, (error, zoom) => {
      if (error) return

      if (zoom > CLUSTER_MAX_ZOOM) {
        source.getClusterLeaves(clusterId, IN_VIEW_LIMIT, 0, (leavesError, leaves) => {
          if (leavesError) return
          this.showStack(leaves, feature.properties.point_count)
        })
      } else {
        this.map.easeTo({ center: feature.geometry.coordinates, zoom })
      }
    })
  }

  // Select a site: draw it selected and load its card into the panel. With
  // `fly`, bring it into view first (an In-view row click).
  select(id, { fly = false } = {}) {
    const feature = this.features.find((f) => f.properties.id === id)
    if (!feature) return

    this.selectedId = id
    this.map.setFilter("site-pin-selected", this.selectedFilter(id))

    if (this.hasSelectedEmptyTarget) this.selectedEmptyTarget.hidden = true
    if (this.hasPreviewTarget) {
      this.previewTarget.src = this.previewUrlValue.replace("__ID__", encodeURIComponent(id))
    }
    // Board 10: a pin opens the sheet to its preview card.
    if (this.sheetMode()) {
      this.setSheet(true)
      this.panelTarget.scrollTop = 0
    }

    if (fly) {
      this.map.flyTo({
        center: feature.geometry.coordinates,
        zoom: Math.max(this.map.getZoom(), CLUSTER_MAX_ZOOM + 1)
      })
    }

    this.highlightRow()
  }

  clearSelection() {
    this.selectedId = null
    this.map.setFilter("site-pin-selected", this.selectedFilter(null))
    if (this.hasSelectedEmptyTarget) this.selectedEmptyTarget.hidden = false
    if (this.hasPreviewTarget) {
      this.previewTarget.removeAttribute("src")
      this.previewTarget.replaceChildren()
    }
  }

  selectedFilter(id) {
    return ["all", ["!", ["has", "point_count"]], ["==", ["get", "id"], id || ""]]
  }

  // An In-view (or stack) row. A stack's members already share the spot on
  // screen — flying would move the camera and swap the stack list back to
  // In view under the member's cursor.
  chooseRow(event) {
    event.preventDefault()
    this.select(event.currentTarget.dataset.siteId, { fly: !this.stack })
  }

  // --------------------------------------------------------------- in view --

  moved() {
    // Panning away from a stack returns the panel to what is in view. The
    // stack's own click does not move the camera, so this is a user move.
    this.stack = null
    this.renderInView()
    this.writeCamera()
  }

  // Sites whose pins fall inside the viewport, from the loaded GeoJSON —
  // clustered or not, a site in view is in view. Name order (the pins
  // arrive A–Z), capped at IN_VIEW_LIMIT.
  renderInView() {
    if (!this.map || this.stack) return

    const bounds = this.map.getBounds()
    const inView = this.features.filter((f) => bounds.contains(f.geometry.coordinates))

    for (const target of this.inViewCountTargets) target.textContent = `${inView.length} in view`
    this.renderRows(inView, "In view")
  }

  showStack(leaves, total) {
    this.stack = leaves
    const sorted = [ ...leaves ].sort((a, b) => a.properties.name.localeCompare(b.properties.name))
    this.renderRows(sorted, `At this spot (${total})`, total)
  }

  renderRows(features, heading, total = features.length) {
    if (!this.hasInViewListTarget) return

    if (this.hasInViewHeadingTarget) this.inViewHeadingTarget.textContent = heading

    const fragment = document.createDocumentFragment()
    for (const feature of features.slice(0, IN_VIEW_LIMIT)) {
      fragment.appendChild(this.row(feature.properties))
    }
    this.inViewListTarget.replaceChildren(fragment)

    if (this.hasInViewMoreTarget) this.inViewMoreTarget.classList.toggle("hidden", total <= IN_VIEW_LIMIT)
    this.highlightRow()
  }

  // Built with textContent only — names and postcodes are user data.
  row({ id, name, postcode, compliance }) {
    const item = document.createElement("li")
    const button = document.createElement("button")
    button.type = "button"
    button.dataset.siteId = id
    button.dataset.action = "sites-map#chooseRow"
    // min-h-11: a 44px touch target in the phone sheet.
    button.className = "flex min-h-11 w-full items-center justify-between gap-2 py-1.5 text-left text-sm lg:min-h-0"

    const label = document.createElement("span")
    label.className = "truncate font-medium text-blue-600 hover:text-blue-700"
    label.dataset.rowLabel = ""
    label.textContent = name

    const code = document.createElement("span")
    code.className = "shrink-0 text-xs text-gray-500"
    code.textContent = postcode || ""

    // Compliance mode: the pin's colour beside the name. A stack's members
    // never show as single pins, so this is where their own status reads.
    if (this.complianceValue && compliance) {
      const dot = document.createElement("span")
      dot.className = "h-2.5 w-2.5 shrink-0 rounded-full"
      dot.style.backgroundColor = COMPLIANCE_COLOURS[compliance] || GRAY_500
      dot.setAttribute("aria-hidden", "true")
      const named = document.createElement("span")
      named.className = "flex min-w-0 items-center gap-2"
      named.append(dot, label)
      button.append(named, code)
    } else {
      button.append(label, code)
    }

    item.appendChild(button)
    return item
  }

  highlightRow() {
    if (!this.hasInViewListTarget) return
    for (const button of this.inViewListTarget.querySelectorAll("button[data-site-id]")) {
      const selected = button.dataset.siteId === this.selectedId
      button.setAttribute("aria-current", selected ? "true" : "false")
      const label = button.querySelector("[data-row-label]")
      label.classList.toggle("text-blue-600", !selected)
      label.classList.toggle("text-blue-800", selected)
      label.classList.toggle("font-semibold", selected)
      label.classList.toggle("font-medium", !selected)
    }
  }
}
